import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { AppService } from '../../core/app.service';
import { IconComponent } from '../../shared/icon/icon.component';
import { openDrawerDialog } from '../../shared/widgets/drawer/drawer.helpers';
import { CxConvListComponent } from './cx-conv-list.component';
import { CxThreadComponent } from './cx-thread.component';
import { CxNewConvoDrawerComponent } from './cx-new-convo-drawer.component';
import { MessagesStore } from '../../data/messages/messages.store';
import { CxConvo, cxTitle } from '../../data/messages/messages.data';

/**
 * Floating chat bubble (bottom-right) + pop-out panel with a compact
 * conversation list ↔ thread. Mounted in the app shell; hidden on the full
 * Messages page and on auth screens.
 */
@Component({
  selector: 'app-chat-launcher',
  standalone: true,
  imports: [IconComponent, CxConvListComponent, CxThreadComponent, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './chat-launcher.component.html',
})
export class ChatLauncherComponent {
  readonly store = inject(MessagesStore);
  private readonly app = inject(AppService);
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);

  readonly open = signal(false);
  readonly view = signal<'list' | 'thread'>('list');

  private readonly route = toSignal(
    this.router.events.pipe(
      filter((e) => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url)),
    { initialValue: this.router.url });

  /** Hide on the full inbox + auth screens. */
  readonly hidden = computed(() => {
    const u = this.route();
    return u.includes('/messages') || u.includes('/signin') || u.includes('/signup');
  });

  readonly flat = computed(() => this.app.tweaks()['chatBubble'] === 'flat');
  readonly tone = computed(() => (this.app.tweaks()['supportTone'] as string) || 'professional');

  openConv(id: string): void { this.store.setActive(id); this.view.set('thread'); }
  title(c: CxConvo): string { return cxTitle(c); }

  openNew(): void {
    const ref = openDrawerDialog<CxNewConvoDrawerComponent, unknown, { openId?: string }>(
      this.dialog, CxNewConvoDrawerComponent, { width: '460px', data: {} });
    ref.afterClosed().subscribe((r) => { if (r?.openId) this.openConv(r.openId); });
  }

  goFullInbox(): void { this.open.set(false); this.app.navigate('messages'); }
}
